/**
 * Il GIN RUMMY dal BFF di Limulo: le sei rotte `/v1/games/ginrummy/*` (`limulo-app/rotte.js` di QGinRummy), iniettate in `limuloApi` come `services/limuloScopa.ts` (stessa base, stessa
 * sessione, stesso refresh), e NON dentro `services/limuloApi.ts`: la zona del gin resta fuori dal file di tutti. Le forme sono in `lib/gamehost/rest.ts` e `lib/gamehost/ticket.ts`
 * (il trasporto comune, ADR-0235 §4), qui se ne passa il dominio; i consumatori sono la Hub (`components/ginrummy/*`) e l'hook della partita (`hooks/useGinRummyMatch.ts`).
 *
 * 🔴 Le POST non si ritentano da sole (`limuloBaseQuery` non ritenta nessuna POST) e non si mettono in cache; le letture non restano in cache oltre la schermata (`keepUnusedDataFor: 0`):
 * un tavolo finito non deve ricomparire dalla cache.
 *
 * `/*#__PURE__*\/` e NIENTE destrutturazione degli hook: i consumatori usano `ginRummyApi.useXxx()` dentro il loro corpo. Cosi, se nessun consumatore sopravvive alla build (la produzione:
 * il flag piega via la Hub e le rotte), il bundler toglie anche questo modulo, e con lui le definizioni delle rotte.
 */
import { limuloApi } from '@/services/limuloApi';
import { corpoDi, entraBody, ingressoOf, mescolataOf, restDi, rivincitaBody, tavoliMieiOf, tavoloCreatoOf } from '@/lib/gamehost/rest';
import type { TavoliMiei, TavoloCreato } from '@/lib/gamehost/rest';
import { ticketBody, ticketGrantOf, ticketRoute } from '@/lib/gamehost/ticket';
import type { TicketGrant } from '@/lib/gamehost/ticket';
import { GINRUMMY_DOMINIO, POSTI_AMMESSI, creaTavoloGinRummyBody } from '@/lib/ginrummy/gioco';
import type { RichiestaTavoloGin } from '@/lib/ginrummy/gioco';

const REST = restDi(GINRUMMY_DOMINIO);
const TICKET_ROUTE = ticketRoute(GINRUMMY_DOMINIO);

export const ginRummyApi = /*#__PURE__*/ limuloApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `ginrummy.table.mine.list` — i tavoli VIVI di chi chiama («I miei tavoli / Riprendi»). */
    getMyGinRummyTables: builder.query<TavoliMiei, void>({
      query: () => ({ url: REST.tavoliMiei }),
      transformResponse: (response: unknown) => tavoliMieiOf(response, POSTI_AMMESSI),
      keepUnusedDataFor: 0,
    }),
    /**
     * `ginrummy.table.create` — «Gioca» (casual), «Invita un amico» (privato) e la partita contro il robot (casual + `livello`): sempre 2 posti.
     * ⚠️ Il campo `robot: { livello }` arriva al game host solo dopo il mirror di app-qginrummy su Limulo staging (qlash-limulo#250, kb#432): finche il BFF lo scarta, il tavolo nasce senza robot.
     */
    createGinRummyTable: builder.mutation<TavoloCreato | null, RichiestaTavoloGin>({
      query: (a) => ({ url: REST.tavoli, method: 'POST', body: creaTavoloGinRummyBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `ginrummy.table.join` — «Entra con un codice»: il posto lo sceglie il game host. */
    joinGinRummyTable: builder.mutation<{ tableId: string } | null, { tableId: string; invito: string; idempotencyKey: string }>({
      query: (a) => ({ url: REST.posti(a.tableId), method: 'POST', body: entraBody(a) }),
      transformResponse: (response: unknown, _meta, a) => ingressoOf(response, a.tableId),
    }),
    /** `ginrummy.table.rematch` — la rivincita di un tavolo FINITO: un tavolo nuovo (201) o quello gia aperto da un altro (200). */
    rematchGinRummyTable: builder.mutation<TavoloCreato | null, { tableId: string; idempotencyKey: string }>({
      query: (a) => ({ url: REST.rivincita(a.tableId), method: 'POST', body: rivincitaBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `ginrummy.table.mescolata.read` — il record di una smazzata finita (`qlash-fair-v1`). La verifica a schermo non e in questa PR: si legge e basta. */
    getGinRummyMescolata: builder.query<Record<string, unknown> | null, { tableId: string; smazzata: number }>({
      query: (a) => ({ url: REST.mescolata(a.tableId, a.smazzata) }),
      transformResponse: (response: unknown) => mescolataOf(response),
      keepUnusedDataFor: 0,
    }),
    /** `ginrummy.session.ticket.issue` — il ticket MONOUSO (30 s) del socket del tavolo: corpo `{ tableId }` (lo scopo lo compone la rotta del kit). Chi lo chiede lo consuma subito. */
    issueGinRummyTicket: builder.mutation<TicketGrant | null, { tableId: string }>({
      query: ({ tableId }) => ({ url: TICKET_ROUTE, method: 'POST', body: ticketBody(tableId) }),
      transformResponse: (response: unknown) => ticketGrantOf(corpoDi(response)),
    }),
  }),
});
